import { Avatar as AvatarPrimitive } from '@base-ui/react/avatar';
import { cn } from 'lib/cn';
import { type ComponentProps } from 'react';

import { getAvatarColor, getColorIndexFromSeed } from './avatar-colors';

type AvatarSize = 'xs' | 'sm' | 'default' | 'lg' | 'xl';
type AvatarShape = 'circle' | 'square';

interface AvatarProps extends Omit<
  AvatarPrimitive.Root.Props,
  'color' | 'size'
> {
  /**
   * Hash this seed into a palette color via `getColorIndexFromSeed`. Best for
   * arbitrary string identities where occasional collisions are OK. For
   * projects, prefer the explicit `color` override with
   * `getAvatarColor(getProjectColorIndex(id, availableProjects))` so the
   * block matches the by-project chart series.
   */
  colorSeed?: string;
  /** Explicit CSS color value. Wins over `colorSeed`. */
  color?: string;
  shape?: AvatarShape;
  size?: AvatarSize;
}

export const Avatar = ({
  className,
  size = 'default',
  shape = 'circle',
  colorSeed,
  color,
  style,
  ...props
}: AvatarProps) => {
  const resolvedColor =
    color ??
    (colorSeed ? getAvatarColor(getColorIndexFromSeed(colorSeed)) : undefined);

  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      data-size={size}
      data-shape={shape}
      style={
        resolvedColor
          ? {
              ...style,
              '--avatar-color': resolvedColor,
              '--avatar-fallback-bg': `color-mix(in oklab, ${resolvedColor} 72%, white)`,
              '--avatar-fallback-fg': 'var(--color-neutral-900)',
            }
          : style
      }
      className={cn(
        'group/avatar relative flex size-8 shrink-0 select-none',
        'data-[size=lg]:size-10 data-[size=sm]:size-6 data-[size=xl]:size-12 data-[size=xs]:size-4',
        'rounded-full data-[shape=square]:rounded-md data-[shape=square]:data-[size=xs]:rounded-sm',
        'bg-[var(--avatar-color,var(--color-surface-secondary))]',
        'after:border-line after:absolute after:inset-0 after:border after:mix-blend-darken dark:after:mix-blend-lighten',
        'after:rounded-full data-[shape=square]:after:rounded-md data-[shape=square]:data-[size=xs]:after:rounded-sm',
        className,
      )}
      {...props}
    />
  );
};
Avatar.displayName = 'Avatar';

export const AvatarImage = ({
  className,
  ...props
}: AvatarPrimitive.Image.Props) => (
  <AvatarPrimitive.Image
    data-slot="avatar-image"
    className={cn(
      'aspect-square size-full object-cover',
      'rounded-full group-data-[shape=square]/avatar:rounded-md group-data-[shape=square]/avatar:group-data-[size=xs]/avatar:rounded-sm',
      className,
    )}
    {...props}
  />
);
AvatarImage.displayName = 'AvatarImage';

export const AvatarFallback = ({
  className,
  ...props
}: AvatarPrimitive.Fallback.Props) => (
  <AvatarPrimitive.Fallback
    data-slot="avatar-fallback"
    className={cn(
      'flex size-full items-center justify-center text-sm text-[var(--avatar-fallback-fg,var(--color-foreground))]',
      'rounded-full group-data-[shape=square]/avatar:rounded-md group-data-[shape=square]/avatar:group-data-[size=xs]/avatar:rounded-sm',
      'bg-[var(--avatar-fallback-bg,var(--color-surface-secondary))]',
      'group-data-[size=sm]/avatar:text-xs',
      className,
    )}
    {...props}
  />
);
AvatarFallback.displayName = 'AvatarFallback';

export const AvatarBadge = ({
  className,
  ...props
}: ComponentProps<'span'>) => (
  <span
    data-slot="avatar-badge"
    className={cn(
      'bg-surface-inverse text-foreground-inverse ring-background absolute right-0 bottom-0 z-10 inline-flex items-center justify-center rounded-full bg-blend-color ring-2 select-none',
      'group-data-[size=sm]/avatar:size-2 group-data-[size=sm]/avatar:[&>svg]:hidden',
      'group-data-[size=default]/avatar:size-2.5 group-data-[size=default]/avatar:[&>svg]:size-2',
      'group-data-[size=lg]/avatar:size-3 group-data-[size=lg]/avatar:[&>svg]:size-2',
      'group-data-[size=xl]/avatar:size-3.5 group-data-[size=xl]/avatar:[&>svg]:size-2.5',
      className,
    )}
    {...props}
  />
);
AvatarBadge.displayName = 'AvatarBadge';

export const AvatarGroup = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    data-slot="avatar-group"
    className={cn(
      'group/avatar-group *:data-[slot=avatar]:ring-background flex -space-x-2 *:data-[slot=avatar]:ring-2',
      className,
    )}
    {...props}
  />
);
AvatarGroup.displayName = 'AvatarGroup';

export const AvatarGroupCount = ({
  className,
  ...props
}: ComponentProps<'div'>) => (
  <div
    data-slot="avatar-group-count"
    className={cn(
      'bg-surface-secondary ring-background relative flex size-8 shrink-0 items-center justify-center rounded-full text-sm text-white ring-2',
      'group-has-data-[size=lg]/avatar-group:size-10 group-has-data-[size=sm]/avatar-group:size-6 group-has-data-[size=xl]/avatar-group:size-12 group-has-data-[size=xs]/avatar-group:size-4',
      '[&>svg]:size-4 group-has-data-[size=lg]/avatar-group:[&>svg]:size-5 group-has-data-[size=sm]/avatar-group:[&>svg]:size-3',
      className,
    )}
    {...props}
  />
);
AvatarGroupCount.displayName = 'AvatarGroupCount';
